import React, { useState, useEffect } from 'react';
import useTrackToolUsage from '@/hooks/useTrackToolUsage';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CurrencyInput from '@/components/CurrencyInput';
import WorkflowStepper from '@/components/WorkflowStepper';
import { Sparkle, Plus, Trash, PencilSimple, X, CheckCircle, FloppyDisk } from '@phosphor-icons/react';

const SOURCE_TYPES = [
  { value: 'employment', label: 'Employment Income' },
  { value: 'self_employment', label: 'Self-Employment Income' },
  { value: 'pension', label: 'Pension' },
  { value: 'social_security', label: 'Social Security' },
  { value: 'investment', label: 'Investment Income' },
  { value: 'rental', label: 'Rental Income' },
  { value: 'other', label: 'Other' },
];

// Legacy → new value display lookup so any pre-existing rows (salary, business,
// freelance, dividend) still render with a sensible label after the rename.
const LEGACY_TYPE_LABELS = {
  salary: 'Employment Income',
  business: 'Self-Employment Income',
  freelance: 'Self-Employment Income',
  dividend: 'Investment Income',
};

const TAX_TREATMENTS = [
  { value: 'taxable', label: 'Taxable', hint: 'Fully taxed as ordinary income (W-2, business profit, rental NOI)' },
  { value: 'tax_deferred', label: 'Tax-deferred', hint: 'Taxed when withdrawn (401(k), Traditional IRA, pension)' },
  { value: 'tax_free', label: 'Tax-free', hint: 'Never taxed (Roth qualified, muni bond interest, HSA qualified)' },
  { value: 'partially_taxable', label: 'Partially taxable / varies', hint: 'Mixed treatment (Social Security up to 85%, qualified dividends, LTCG)' },
];

const TAX_TREATMENT_LABEL = Object.fromEntries(TAX_TREATMENTS.map(t => [t.value, t.label]));

const sourceTypeLabel = (val) => (
  SOURCE_TYPES.find(t => t.value === val)?.label ||
  LEGACY_TYPE_LABELS[val] ||
  val ||
  '—'
);

// Back-compat: if an existing row has `is_taxable` but no `tax_treatment`, infer one.
const inferTaxTreatment = (row) => {
  if (row.tax_treatment && TAX_TREATMENT_LABEL[row.tax_treatment]) return row.tax_treatment;
  return row.is_taxable ? 'taxable' : 'tax_free';
};

const blankForm = { name: '', source_type: '', monthly_amount: null, tax_treatment: 'taxable' };

const IncomeSources = () => {
  useTrackToolUsage('income_sources');
  const [sources, setSources] = useState([]);
  const [formData, setFormData] = useState(blankForm);
  const [savedAt, setSavedAt] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchSources(); }, []);

  const fetchSources = async () => {
    try {
      const response = await api.get('/income-sources');
      setSources(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load income sources');
    }
  };

  const flashSaved = (label) => {
    setSavedAt({ label, ts: Date.now() });
    setTimeout(() => setSavedAt((s) => (s && Date.now() - s.ts >= 4000 ? null : s)), 4500);
  };

  const validate = (data) => {
    if (!data.name?.trim()) return 'Please enter a name (e.g. "Primary Job")';
    if (!data.source_type) return 'Please select a source type';
    if (data.monthly_amount === null || data.monthly_amount === undefined || Number.isNaN(data.monthly_amount)) {
      return 'Please enter a monthly amount — commas and $ are okay';
    }
    if (data.monthly_amount < 0) return 'Amount cannot be negative';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate(formData);
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await api.post('/income-sources', {
        ...formData,
        is_taxable: ['taxable', 'partially_taxable'].includes(formData.tax_treatment),
      });
      toast.success('Income source added');
      setFormData(blankForm);
      flashSaved('Saved');
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add income source');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/income-sources/${id}`);
      toast.success('Source deleted');
      flashSaved('Deleted');
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete source');
    }
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditForm({
      name: s.name,
      source_type: s.source_type,
      monthly_amount: s.monthly_amount,
      tax_treatment: inferTaxTreatment(s),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(blankForm);
  };

  const saveEdit = async (id) => {
    const err = validate(editForm);
    if (err) { toast.error(err); return; }
    try {
      await api.put(`/income-sources/${id}`, {
        ...editForm,
        is_taxable: ['taxable', 'partially_taxable'].includes(editForm.tax_treatment),
      });
      toast.success('Income source updated');
      flashSaved('Saved');
      setEditingId(null);
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update source');
    }
  };

  const totalMonthly = sources.reduce((sum, s) => sum + s.monthly_amount, 0);
  const totalAnnual = totalMonthly * 12;
  const taxableMonthly = sources.filter(s => s.is_taxable).reduce((sum, s) => sum + s.monthly_amount, 0);

  return (
    <div className="space-y-8 fade-in">
      <div className="bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="duotone" className="text-[#C5A059] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[#4A5568]">
            <strong className="text-[#1A202C]">Educational Tool:</strong> Track multiple income streams. Consult a tax professional regarding taxable vs non-taxable income classification.
          </p>
        </div>
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="cabinet-grotesk text-3xl sm:text-4xl tracking-tight font-medium text-[#0A1128] mb-2">Income Sources</h1>
          <p className="text-base text-[#4A5568]">Manage your multiple income streams</p>
        </div>
        {savedAt && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs font-semibold uppercase tracking-[0.08em] rounded" data-testid="income-saved-indicator">
            <CheckCircle size={14} weight="fill" /> {savedAt.label}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Monthly Total</p>
          <p className="text-3xl font-semibold text-[#0A1128] ibm-plex">${totalMonthly.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Annual Total</p>
          <p className="text-3xl font-semibold text-[#C5A059] ibm-plex">${totalAnnual.toLocaleString()}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#4A5568] mb-2">Taxable / Mo</p>
          <p className="text-3xl font-semibold text-[#2D6A4F] ibm-plex">${taxableMonthly.toLocaleString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Add Income Source</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="income-name-input" placeholder="Primary Job" value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="border-slate-300" />
              </div>
              <div>
                <Label htmlFor="source_type">Type</Label>
                <Select value={formData.source_type} onValueChange={(value) => setFormData({ ...formData, source_type: value })}>
                  <SelectTrigger data-testid="income-type-select" className="border-slate-300">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="monthly_amount">Monthly Amount</Label>
                <CurrencyInput
                  value={formData.monthly_amount}
                  onValue={(v) => setFormData((p) => ({ ...p, monthly_amount: v }))}
                  testid="income-amount-input"
                  placeholder="$5,000"
                  required
                />
                <p className="text-xs text-[#A0AABF] mt-1">Type naturally — commas and $ are okay.</p>
              </div>
              <div>
                <Label htmlFor="tax_treatment">Tax Status</Label>
                <Select value={formData.tax_treatment} onValueChange={(value) => setFormData({ ...formData, tax_treatment: value })}>
                  <SelectTrigger data-testid="income-tax-treatment-select" className="border-slate-300"><SelectValue placeholder="Select tax status" /></SelectTrigger>
                  <SelectContent>
                    {TAX_TREATMENTS.map(t => (
                      <SelectItem key={t.value} value={t.value} data-testid={`tax-treatment-option-${t.value}`}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#A0AABF] mt-1">
                  {TAX_TREATMENTS.find(t => t.value === formData.tax_treatment)?.hint || ''}
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={submitting} data-testid="add-income-button" className="flex-1 bg-[#0A1128] hover:bg-[#152145]">
                  <Plus size={18} className="mr-2" /> {submitting ? 'Adding…' : 'Add Source'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setFormData(blankForm)} data-testid="cancel-add-income-button" className="border-slate-300">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white border border-slate-200 rounded-md shadow-sm p-6 dashboard-card">
            <h2 className="cabinet-grotesk text-xl font-medium text-[#0A1128] mb-4">Your Income Sources</h2>
            {sources.length === 0 ? (
              <p className="text-center text-[#4A5568] py-8">No income sources yet. Add your first one!</p>
            ) : (
              <div className="space-y-3">
                {sources.map(source => editingId === source.id ? (
                  <div key={source.id} className="border-2 border-[#C5A059] rounded-md p-4 space-y-3 bg-[#C5A059]/5" data-testid={`income-edit-row-${source.id}`}>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Name</Label>
                        <Input value={editForm.name} data-testid={`edit-name-${source.id}`} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="border-slate-300" />
                      </div>
                      <div>
                        <Label className="text-xs">Type</Label>
                        <Select value={editForm.source_type} onValueChange={(v) => setEditForm({ ...editForm, source_type: v })}>
                          <SelectTrigger data-testid={`edit-type-${source.id}`} className="border-slate-300"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SOURCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Monthly Amount</Label>
                        <CurrencyInput
                          value={editForm.monthly_amount}
                          onValue={(v) => setEditForm((p) => ({ ...p, monthly_amount: v }))}
                          testid={`edit-amount-${source.id}`}
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Tax Status</Label>
                        <Select value={editForm.tax_treatment} onValueChange={(v) => setEditForm({ ...editForm, tax_treatment: v })}>
                          <SelectTrigger data-testid={`edit-tax-treatment-${source.id}`} className="border-slate-300"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TAX_TREATMENTS.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={cancelEdit} data-testid={`cancel-edit-${source.id}`} className="border-slate-300">
                        <X size={14} className="mr-1" /> Cancel
                      </Button>
                      <Button type="button" onClick={() => saveEdit(source.id)} data-testid={`save-edit-${source.id}`} className="bg-[#0A1128] hover:bg-[#152145]">
                        <FloppyDisk size={14} className="mr-1" /> Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div key={source.id} className="border border-slate-200 rounded-md p-4 flex items-center justify-between" data-testid={`income-source-${source.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h3 className="font-medium text-[#0A1128]">{source.name}</h3>
                        {(() => {
                          const tt = inferTaxTreatment(source);
                          const isFullyTaxable = tt === 'taxable';
                          const isFullyTaxFree = tt === 'tax_free';
                          const colorCls = isFullyTaxable
                            ? 'bg-[#C5A059]/10 text-[#B7791F]'
                            : isFullyTaxFree
                              ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]'
                              : 'bg-slate-100 text-[#4A5568]';
                          return (
                            <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${colorCls}`} data-testid={`tax-badge-${source.id}`}>
                              {TAX_TREATMENT_LABEL[tt] || 'Taxable'}
                            </span>
                          );
                        })()}
                      </div>
                      <p className="text-sm text-[#4A5568]">{sourceTypeLabel(source.source_type)}</p>
                    </div>
                    <div className="text-right mr-4">
                      <p className="text-lg font-semibold text-[#0A1128] ibm-plex">${source.monthly_amount.toLocaleString()}</p>
                      <p className="text-xs text-[#4A5568]">/month</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(source)} data-testid={`edit-income-${source.id}`} className="text-[#4A5568] hover:text-[#0A1128] p-1.5 rounded hover:bg-slate-100" aria-label="Edit">
                        <PencilSimple size={18} />
                      </button>
                      <button onClick={() => handleDelete(source.id, source.name)} data-testid={`delete-income-${source.id}`} className="text-[#C53030] hover:text-[#9B2C2C] p-1.5 rounded hover:bg-[#C53030]/10" aria-label="Delete">
                        <Trash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <WorkflowStepper testid="income-workflow-stepper" />
    </div>
  );
};

export default IncomeSources;
